import React from 'react';
import Icon from '../../../components/AppIcon';
import ComplianceStatusBadge from '../../admin-dashboard/components/ComplianceStatusBadge';
import { useLanguage } from '../../../contexts/LanguageContext';

const ComplianceSummary = ({ 
  status, 
  totalDocuments, 
  compliancePercentage, 
  compliantCount, 
  expiringCount, 
  expiredCount 
}) => {
  const { t } = useLanguage();

  const getStatusConfig = (status) => {
    switch (status) {
      case 'compliant':
        return {
          icon: 'CheckCircle2',
          color: 'text-success',
          bgColor: 'bg-success/10',
          message: t('allComplianceRequirementsMet')
        };
      case 'warning':
        return {
          icon: 'AlertTriangle',
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          message: t('someDocumentsExpiringSoon')
        };
      case 'critical':
        return {
          icon: 'AlertCircle',
          color: 'text-error',
          bgColor: 'bg-error/10',
          message: t('immediateActionRequired')
        };
      default:
        return {
          icon: 'HelpCircle',
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          message: t('statusUnavailable')
        };
    }
  };

  const statusConfig = getStatusConfig(status);

  return (
    <div className="bg-card border border-border rounded-lg p-6 md:p-8 shadow-elevation-2 print:shadow-none print:border-2">
      <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">{t('complianceSummary')}</h2>
      
      <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border">
        <div className={`flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full ${statusConfig?.bgColor}`}>
          <Icon name={statusConfig?.icon} size={24} className={statusConfig?.color} />
        </div>
        <div className="flex-1">
          <div className="mb-2">
            <ComplianceStatusBadge status={status} />
          </div>
          <p className="text-sm text-muted-foreground">{statusConfig?.message}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">{t('overallCompliance')}</span>
            <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
          </div>
          <p className="text-3xl font-bold text-foreground">{compliancePercentage}%</p>
          <div className="mt-3 w-full bg-background rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all ${
                compliancePercentage >= 80 ? 'bg-success' : 
                compliancePercentage >= 50 ? 'bg-warning' : 'bg-error'
              }`}
              style={{ width: `${compliancePercentage}%` }}
            />
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">{t('totalDocuments')}</span>
            <Icon name="FileText" size={16} className="text-muted-foreground" />
          </div>
          <p className="text-3xl font-bold text-foreground">{totalDocuments}</p>
          <p className="text-xs text-muted-foreground mt-2">{t('trackedDocuments')}</p>
        </div>

        <div className="bg-success/10 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-success uppercase tracking-wide font-medium">{t('compliant')}</span>
            <Icon name="CheckCircle2" size={16} className="text-success" />
          </div>
          <p className="text-3xl font-bold text-success">{compliantCount}</p>
          <p className="text-xs text-success/80 mt-2">{t('validDocuments')}</p>
        </div>

        <div className="bg-warning/10 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-warning uppercase tracking-wide font-medium">{t('expiring')}</span>
            <Icon name="Clock" size={16} className="text-warning" />
          </div>
          <p className="text-3xl font-bold text-warning">{expiringCount}</p>
          <p className="text-xs text-warning/80 mt-2">{t('actionNeeded')}</p>
        </div>
      </div>

      {expiredCount > 0 && (
        <div className="mt-4 bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-error">{t('criticalAlert')}</p>
            <p className="text-sm text-error/80 mt-1">
              {expiredCount} {expiredCount === 1 ? t('documentHasExpired') : t('documentsHaveExpired')}. {t('immediateActionRequired')}.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceSummary;